import { SelfServiceableAccounts } from 'src/app/feature-modules/accounts/models/accounts.model';
import { NavItem } from './header-nav.model';
import { LOAN_CPID } from 'src/app/config/account.config';
import { environment } from 'src/environments/environment';
import { Offer } from 'src/app/feature-modules/offers/models/offers.model';
import { getAccountDirectLink, getLoanAccountPaperlessData } from 'src/app/feature-modules/accounts/util/accounts.util';
import { ProfilePaperlessNormalized } from '../../feature-modules/profile/models/profile-paperless.model';

// Nav item mappers
export const mapLoanNavItem = (
  accounts: SelfServiceableAccounts,
  paperlessProfiles: ProfilePaperlessNormalized,
  loanError: string,
  hasPastDueAccount: boolean
): NavItem => {
  const res: NavItem = {
    title: 'Personal Loan',
    type: 'loan',
    isSubMenuExpanded: false,
    isErrorExpanded: false,
    subItemsMobileHeight: '0',
    subItems: [],
    isActive: false,
    isDisabled: false,
    isExternalLink: false,
    isSlideNavDelay: false,
    url: '',
    reinstatedDate: '',
    repossessionCaseStatus: '',
  };
  const loanUrl = accounts?.loanAccounts?.[0]?.url || '';

  // Loan Error
  if (loanError) {
    return { ...res, isDisabled: true };
  }

  // No loan accts
  if (!accounts.loanAccounts.length || accounts.loanAccounts[0].accountDetailsList.length === 0) {
    return {
      ...res,
      url: `${environment.loanAuthenticatedUrl}/?cpid=${LOAN_CPID}&utm_source=auth_nav`,
      isExternalLink: true,
    };
  }

  // Single acct
  if (accounts.loanAccounts[0].accountDetailsList.length === 1) {
    const { repossessionCaseStatus, reinstatedDate } = getLoanAccountPaperlessData(
      accounts.loanAccounts[0].accountDetailsList[0],
      paperlessProfiles
    );
    return {
      ...res,
      last4DigitsExternalAccountNumber: accounts.loanAccounts[0].accountDetailsList[0].last4DigitsExternalAccountNumber,
      productType: accounts.loanAccounts[0].accountDetailsList[0].productType,
      accountStatus: accounts.loanAccounts[0].accountDetailsList[0].accountStatus,
      repossessionCaseStatus: repossessionCaseStatus,
      reinstatedDate: reinstatedDate,
      url: hasPastDueAccount
        ? loanUrl
        : getAccountDirectLink(loanUrl, 'dashboard', accounts.loanAccounts[0].accountDetailsList[0].id),
    };
  }

  // Multiple accts
  if (accounts.loanAccounts[0].accountDetailsList.length > 1) {
    const subItems: NavItem[] = accounts.loanAccounts[0].accountDetailsList.map((account) => {
      const { repossessionCaseStatus, reinstatedDate } = getLoanAccountPaperlessData(account, paperlessProfiles);
      return {
        title: `Account....${account.last4DigitsExternalAccountNumber}`,
        type: 'loan',
        isActive: false,
        isDisabled: false,
        isExternalLink: false,
        isSlideNavDelay: false,
        accountStatus: account.accountStatus,
        productType: account.productType,
        last4DigitsExternalAccountNumber: account.last4DigitsExternalAccountNumber,
        repossessionCaseStatus: repossessionCaseStatus,
        reinstatedDate: reinstatedDate,
        url: hasPastDueAccount ? loanUrl : getAccountDirectLink(loanUrl, 'dashboard', account.id),
      };
    });
    return { ...res, subItems };
  }

  //All other cases
  return { ...res, url: '' };
};

export const mapCardNavItem = (accounts: SelfServiceableAccounts, offers: Offer[], cardError: string): NavItem => {
  const res: NavItem = {
    title: 'Credit Card',
    type: 'card',
    isSubMenuExpanded: false,
    isErrorExpanded: false,
    subItemsMobileHeight: '0',
    subItems: [],
    isActive: false,
    isDisabled: false,
    isExternalLink: true,
    isSlideNavDelay: false,
    url: '',
  };

  // Error
  if (cardError) {
    return { ...res, isDisabled: true };
  }

  // No card accts
  if (accounts.cardAccounts.length === 0) {
    const offerUrl = offers.find((offer) => offer.productType === 'CREDIT_CARD')?.offerUrl ?? '';
    return { ...res, url: offerUrl };
  }

  // Single card acct
  if (accounts.cardAccounts.length === 1) {
    return {
      ...res,
      saml: accounts.cardAccounts[0].saml,
      secondLevelValidated: accounts.cardAccounts[0].secondLevelValidated,
      lastFourCardNumber: accounts.cardAccounts[0].lastFourCardNumber,
    };
  }

  // Multiple card accts
  if (accounts.cardAccounts.length > 1) {
    const subItems: NavItem[] = accounts.cardAccounts.map((account) => ({
      title: `Best Egg....${account.lastFourCardNumber}`,
      lastFourCardNumber: account.lastFourCardNumber,
      type: 'card',
      secondLevelValidated: account.secondLevelValidated,
      saml: account.saml,
      isActive: false,
      isDisabled: false,
      isExternalLink: true,
      isSlideNavDelay: false,
    }));
    return { ...res, subItems };
  }

  return { ...res, url: '' };
};

export const mapBefhNavItem = (accounts: SelfServiceableAccounts, befhError: string): NavItem => {
  const res: NavItem = {
    title: 'Financial Health',
    type: 'befh',
    isActive: false,
    isDisabled: false,
    url: '',
    isSubMenuExpanded: false,
    isExternalLink: false,
    isSlideNavDelay: false,
  };

  if (befhError) {
    return { ...res, isDisabled: true };
  }
  return {
    ...res,
    url: accounts.bechAccounts[0].url,
    kbaCompleted: accounts.bechAccounts[0].kbaCompleted,
  };
};

export const mapOfferNavItem = (offers: Offer[] = []): NavItem => ({
  title: 'Offers',
  type: 'offers',
  isActive: false,
  isDisabled: false,
  isExternalLink: false,
  isSlideNavDelay: false,
  offerCount: offers.length,
});
